import { useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircle,
  Zap,
  RotateCw,
  Coins,
  Users,
  Activity,
  Gauge,
  CalendarDays,
  Trophy,
  Compass,
  Check,
  Shield,
  BookOpen,
  Wrench,
  Brain,
  Timer,
  BarChart3,
  ChevronRight,
  ChevronDown,
  Layers,
  Target,
} from "lucide-react";
import {
  Chart as ChartJS,
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import CompanyUserLayout from "../../layouts/CompanyUserLayout";
import StatusDot from "../../components/common/StatusDot";
import "../../assets/styles/colors.css";
import { useAuth } from "../../context/AuthContext";
import {
  getMyOverview,
  normalizeMyOverview,
  getTokenActivity,
  normalizeTokenActivity,
  getSoloProgression,
  normalizeSoloProgression,
  getCategoryCoverage,
  normalizeCategoryCoverage,
  getMyBoxes,
  normalizeMyBoxes,
  getTksaProgress,
  normalizeTksaProgress,
  getDifficultyStats,
  normalizeDifficultyStats,
  getTksaBreakdown,
  normalizeTksaBreakdown,
} from "../../api/services/companyUserDashboardService";

ChartJS.register(
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
);

/* ─────────────────────────────────────────────
   Theme bridge — Chart.js needs concrete colors, not CSS vars, so we read
   the real computed --color-* values at runtime. A MutationObserver on the
   <html> class re-snapshots whenever the theme toggles — without it the
   colours captured at mount stay stale until a full page refresh.
   ───────────────────────────────────────────── */
function useThemeColors() {
  const [c, setC] = useState(null);
  useEffect(() => {
    const readColors = () => {
      const root = getComputedStyle(document.documentElement);
      const v = (name, fallback) =>
        (root.getPropertyValue(name) || "").trim() || fallback;
      setC({
        primary: v("--color-primary", "#00e639"),
        primaryHover: v("--color-primary-hover", "#00c430"),
        accentViolet: v("--color-accent-violet", "#8b5cf6"),
        textPrimary: v("--color-text-primary", "#dfe2eb"),
        textSecondary: v("--color-text-secondary", "#8fa3be"),
        textMuted: v("--color-text-muted", "#3e4f65"),
        border: v("--color-border", "#212a38"),
        surface: v("--color-surface", "#181c22"),
        bg: v("--color-bg", "#0f1219"),
        blue: "#3b82f6",
        violet: "#8b5cf6",
        orange: "#f59e0b",
        red: "#ef4444",
      });
    };

    readColors();
    const observer = new MutationObserver(readColors);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  return c;
}

function withAlpha(hex, a) {
  const m = hex.replace("#", "");
  const n =
    m.length === 3
      ? m
          .split("")
          .map((x) => x + x)
          .join("")
      : m;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

function useCountUp(target, run, dur = 1300) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setVal(target * e);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, run, dur]);
  return Math.round(val).toLocaleString();
}

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const fmtK = (v) =>
  v >= 1000 ? (v / 1000).toFixed(1).replace(/\.0$/, "") + "K" : String(v);

/* ─────────────────────────────────────────────
   Dummy data (swap for API data later)
   ───────────────────────────────────────────── */
const DATA = {
  boxesCompleted: 31,
  boxesAttempted: 47,
  firstTrySuccess: 72,
  boxesCreated: 23,
  created: { approved: 19, pending: 2, rejected: 2 },
  tokensRemaining: 41600,
  tokensUsedToday: 8400,
  dailyCap: 50000,
  team: { name: "Alpha Squad", active: 2, members: 8 },
  teamAvatars: [
    { initials: "MA", tint: "primary" },
    { initials: "RK", tint: "blue" },
    { initials: "SL", tint: "violet" },
    { initials: "TH", tint: "orange" },
  ],
  tokenTrend: [
    4200, 5800, 7100, 5400, 6900, 8400, 7600, 9200, 8100, 10400, 7800, 9800,
    11200, 8600, 10100, 12300, 9400, 11700, 13100, 10200, 12800, 14400, 11100,
    13600, 15200, 12400, 14900, 16100, 13800, 8400,
  ],
  conversations: 47,
  attemptCumulative: [
    0, 1, 0, 2, 1, 0, 1, 2, 0, 1, 2, 1, 0, 2, 1, 1, 0, 2, 1, 0, 2, 1, 1, 2, 0,
    1, 2, 1, 2, 1,
  ],
  completeCumulative: [
    0, 0, 1, 1, 0, 0, 1, 1, 0, 1, 1, 0, 0, 1, 1, 0, 0, 1, 1, 0, 1, 1, 0, 1, 0,
    1, 1, 1, 1, 1,
  ],
  completion: { complete: 31, untouched: 8, attempted: 47, pct: 66 },
  categories: {
    labels: [
      "Social Engineering",
      "Web Security",
      "Cryptography",
      "Network Defense",
      "Cloud Security",
      "Reverse Engineering",
      "Forensics",
      "Threat Intelligence",
    ],
    values: [90, 85, 75, 60, 40, 30, 15, 0],
  },
  mastered: ["Social Engineering", "Web Security"],
  untouched: ["Threat Intelligence"],
  boxRuns: [
    { name: "Advanced_XSS_Bypass_Box", time: "2h ago", status: "approved" },
    { name: "SQL_Injection_CTF_Level3", time: "Yesterday", status: "approved" },
    { name: "Linux_PrivEsc_Challenge", time: "2 days ago", status: "approved" },
    { name: "APT_Simulation_Beta", time: "3 days ago", status: "pending" },
    { name: "Phishing_Kit_Analysis", time: "4 days ago", status: "approved" },
    { name: "Network_Recon_Advanced", time: "5 days ago", status: "rejected" },
  ],
};

const TINT_HEX = {
  primary: "#00e639",
  blue: "#3b82f6",
  violet: "#8b5cf6",
  orange: "#f59e0b",
  red: "#ef4444",
};

const STATUS_CFG = {
  approved: {
    color: "var(--color-primary)",
    bg: "var(--color-primary)/10",
    border: "var(--color-primary)/20",
    label: "APPROVED",
  },
  pending: {
    color: "#f59e0b",
    bg: "rgb(245 158 11 / 0.12)",
    border: "rgb(245 158 11 / 0.2)",
    label: "PENDING",
  },
  rejected: {
    color: "#ef4444",
    bg: "rgb(239 68 68 / 0.12)",
    border: "rgb(239 68 68 / 0.2)",
    label: "REJECTED",
  },
};

/* ── Building blocks ─────────────────────────── */
function SectionLabel({ children }) {
  return (
    <div className="text-[10.5px] tracking-[0.22em] uppercase font-semibold text-(--color-text-muted) font-mono">
      {children}
    </div>
  );
}

function Card({ children, className = "", style }) {
  return (
    <div className={`qv-card qv-noise ${className}`} style={style}>
      {children}
    </div>
  );
}

function Avatar({ initials, tint, extra }) {
  const hex = TINT_HEX[tint] || "#8fa3be";
  const isPrimary = tint === "primary";
  return (
    <span
      className={`w-7 h-7 rounded-full inline-flex items-center justify-center font-bold shrink-0 border-2 border-(--color-surface) ${
        extra ? "text-[9px]" : "text-[10px]"
      }`}
      style={{
        background: extra
          ? withAlpha("#8fa3be", 0.2)
          : isPrimary
            ? "color-mix(in srgb, var(--color-primary) 18%, transparent)"
            : withAlpha(hex, 0.18),
        color: extra
          ? "var(--color-text-secondary)"
          : isPrimary
            ? "var(--color-primary)"
            : hex,
      }}
    >
      {initials}
    </span>
  );
}

function tipPreset(colors) {
  return {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderWidth: 1,
    titleColor: colors.textPrimary,
    bodyColor: colors.textSecondary,
    padding: 10,
    cornerRadius: 8,
    displayColors: true,
    boxWidth: 8,
    boxHeight: 8,
    boxPadding: 4,
  };
}

/* Hover-gated glow for line charts — glows the whole line while the mouse
   is over the canvas. Mirrors the other dashboards. Falls back to glowColor
   when borderColor is a gradient function. */
const lineGlowPlugin = {
  id: "lineGlow",
  afterDatasetsDraw(chart) {
    if (!chart._lineGlowHover) return;
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, i) => {
      const meta = chart.getDatasetMeta(i);
      if (meta.hidden || meta.type !== "line" || !meta.dataset) return;
      const glow =
        dataset.glowColor ||
        (typeof dataset.borderColor === "string"
          ? dataset.borderColor
          : "#ffffff");
      [8, 18].forEach((blur) => {
        ctx.save();
        ctx.shadowColor = glow;
        ctx.shadowBlur = blur;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        meta.dataset.draw(ctx, chart.chartArea);
        ctx.restore();
      });
    });
  },
  afterEvent(chart, args) {
    const was = chart._lineGlowHover;
    const now = args.event.type !== "mouseout";
    if (was !== now) {
      chart._lineGlowHover = now;
      chart.draw();
    }
  },
};

/* Glow plugin for bar charts — glows the hovered bar */
const barGlowPlugin = {
  id: "barGlow",
  afterDatasetsDraw(chart) {
    const active = chart.getActiveElements();
    if (!active.length) return;
    const { ctx } = chart;
    active.forEach(({ element, datasetIndex, index }) => {
      const ds = chart.data.datasets[datasetIndex];
      const color = Array.isArray(ds.backgroundColor)
        ? ds.backgroundColor[index]
        : ds.backgroundColor;
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = 22;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      element.draw(ctx);
      ctx.restore();
    });
  },
};

/* ─────────────────────────────────────────────
   Section: My Overview (5 stat cards)
   ───────────────────────────────────────────── */
function MyOverview({ colors }) {
  const [ref, inView] = useInView();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getMyOverview();
        if (!cancelled) setData(normalizeMyOverview(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const completed = useCountUp(data?.boxesCompleted ?? 0, run);
  const created = useCountUp(data?.boxesCreated ?? 0, run);
  const tokens = useCountUp(data?.tokensRemaining ?? 0, run);

  /* ── Error state ───────────────────────────── */
  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>My Overview</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  /* ── Loading skeletons ─────────────────────── */
  if (loading || !data) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>My Overview</SectionLabel>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <Card key={i} className="p-5.5">
              <div className="animate-pulse flex flex-col gap-3">
                <div className="flex items-center gap-2.75">
                  <div className="w-10 h-10 rounded-full bg-white/5" />
                  <div className="h-3 w-28 rounded bg-white/5" />
                </div>
                <div className="h-9 w-24 rounded bg-white/5" />
                <div className="h-8 w-full rounded bg-white/5" />
              </div>
            </Card>
          ))}
        </div>
      </section>
    );
  }

  /* ── Derived values ────────────────────────── */
  const totalCreated =
    data.created.approved + data.created.pending + data.created.rejected || 1;
  const capUsedPct =
    data.dailyCap > 0
      ? Math.round((data.tokensUsedToday / data.dailyCap) * 100)
      : 0;
  const capLeftPct = 100 - capUsedPct;

  const sparkPath =
    "M0,28 L18,26 L36,27 L54,22 L72,23 L90,18 L108,19 L126,14 L144,13 L162,9 L180,7 L200,4";

  const segTotal = 10;
  const segFilled = Math.round((data.firstTrySuccess / 100) * segTotal);

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>My Overview</SectionLabel>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4.5">
        {/* ── Boxes Completed ────────────────── */}
        <Card
          className="p-5 qv-ping"
          style={{ "--ping-x": "50%", "--ping-y": "50%" }}
        >
          <div className="flex items-center gap-2.75 mb-3.5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-(--color-primary)/15">
              <CheckCircle size={20} className="text-(--color-primary)" />
            </div>
            <span className="text-[13px] text-(--color-text-primary)">
              Boxes Completed
            </span>
          </div>
          <div className="qv-num text-[36px] mb-1.5 text-(--color-text-primary)">
            {completed}
          </div>
          <div className="text-[12.5px] text-(--color-text-muted)">
            out of{" "}
            <span className="text-(--color-text-secondary) font-semibold">
              {data.boxesAttempted}
            </span>{" "}
            attempted
          </div>
          <svg
            viewBox="0 0 200 34"
            preserveAspectRatio="none"
            className="w-full h-8.5 mt-3 transition-[filter] duration-300 hover:[filter:drop-shadow(0_0_4px_var(--color-primary))_drop-shadow(0_0_10px_var(--color-primary))]"
          >
            <defs>
              <linearGradient id="cu-spark" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor={withAlpha(colors.primary, 0.28)} />
                <stop offset="1" stopColor={withAlpha(colors.primary, 0)} />
              </linearGradient>
            </defs>
            <path d={`${sparkPath} L200,34 L0,34 Z`} fill="url(#cu-spark)" />
            <path
              d={sparkPath}
              fill="none"
              stroke={colors.primary}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Card>

        {/* ── First-Try Success ──────────────── */}
        <Card className="p-5.5">
          <div className="flex items-center gap-2.75 mb-3.5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-500/12">
              <Zap size={20} className="text-blue-400" />
            </div>
            <span className="text-[13px] text-(--color-text-primary)">
              First-Try Success
            </span>
          </div>
          <div className="qv-num text-[36px] mb-1.5 text-blue-400">
            {data.firstTrySuccess}%
          </div>
          <div className="text-[12.5px] text-(--color-text-muted)">
            solved on the{" "}
            <span className="text-(--color-text-secondary) font-semibold">
              first run
            </span>
          </div>
          <div className="flex items-end gap-0.75 h-8.5 mt-3 transition-[filter] duration-300 hover:[filter:drop-shadow(0_0_4px_#3b82f6)_drop-shadow(0_0_10px_#3b82f6)]">
            {Array.from({ length: segTotal }).map((_, i) => {
              const on = i < segFilled;
              return (
                <span
                  key={i}
                  className="flex-1 rounded-[1px] transition-all duration-500"
                  style={{
                    height: run ? (on ? `${12 + i * 2.2}px` : "10px") : "4px",
                    background: on
                      ? colors.blue
                      : withAlpha(colors.textPrimary, 0.12),
                    transitionDelay: `${0.35 + i * 0.05}s`,
                  }}
                />
              );
            })}
          </div>
        </Card>

        {/* ── Boxes Created ──────────────────── */}
        <Card className="p-5.5 flex flex-col">
          <div className="flex items-center gap-2.75 mb-3.5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-violet-500/12">
              <RotateCw size={20} className="text-violet-400" />
            </div>
            <span className="text-[13px] text-(--color-text-primary)">
              Boxes Created
            </span>
          </div>
          <div className="qv-num text-[36px] mb-1.5 text-(--color-text-primary)">
            {created}
          </div>
          <div className="text-[12.5px] text-(--color-text-muted)">
            <span className="text-(--color-primary) font-semibold">
              {data.created.approved} approved
            </span>{" "}
            · {data.created.pending} pending · {data.created.rejected} rejected
          </div>
          <div className="mt-auto pt-3">
            <div className="flex h-1.5 rounded-full overflow-hidden bg-black/20 gap-px transition-[filter] duration-150 hover:[filter:drop-shadow(0_0_4px_var(--color-primary))_drop-shadow(0_0_8px_var(--color-primary))]">
              <div
                className="h-full transition-all duration-700"
                style={{
                  width: run
                    ? `${(data.created.approved / totalCreated) * 100}%`
                    : 0,
                  background: colors.primary,
                }}
              />
              <div
                className="h-full transition-all duration-700"
                style={{
                  width: run
                    ? `${(data.created.pending / totalCreated) * 100}%`
                    : 0,
                  background: colors.orange,
                }}
              />
              <div
                className="h-full transition-all duration-700"
                style={{
                  width: run
                    ? `${(data.created.rejected / totalCreated) * 100}%`
                    : 0,
                  background: colors.red,
                }}
              />
            </div>
            <div className="flex gap-3 mt-2 text-[9.5px] text-(--color-text-muted)">
              <span className="flex items-center gap-1">
                <i className="w-1.5 h-1.5 rounded-[1px] bg-(--color-primary)" />
                Approved
              </span>
              <span className="flex items-center gap-1">
                <i className="w-1.5 h-1.5 rounded-[1px] bg-orange-500" />
                Pending
              </span>
              <span className="flex items-center gap-1">
                <i className="w-1.5 h-1.5 rounded-[1px] bg-red-500" />
                Rejected
              </span>
            </div>
          </div>
        </Card>

        {/* ── Tokens Remaining ───────────────── */}
        <Card className="p-5.5 flex flex-col">
          <div className="flex items-center gap-2.75 mb-3.5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-orange-500/12">
              <Coins size={20} className="text-orange-400" />
            </div>
            <span className="text-[13px] text-(--color-text-primary)">
              Tokens Remaining
            </span>
          </div>
          <div className="qv-num text-[36px] mb-1.5 text-orange-500">
            {tokens}
          </div>
          <div className="text-[12.5px] text-(--color-text-muted)">
            of {fmtK(data.dailyCap)} daily cap
          </div>
          <div className="mt-auto pt-3">
            <div className="h-1.5 rounded-full overflow-hidden bg-black/20 transition-[filter] duration-150 hover:[filter:drop-shadow(0_0_4px_#f59e0b)_drop-shadow(0_0_8px_#f59e0b)]">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: run ? `${capUsedPct}%` : 0,
                  background: colors.orange,
                }}
              />
            </div>
            <div className="flex justify-between mt-2 text-[10.5px] text-(--color-text-muted)">
              <span>{fmtK(data.tokensUsedToday)} used today</span>
              <span className="text-orange-500 font-semibold">
                {capLeftPct}% left
              </span>
            </div>
          </div>
        </Card>

        {/* ── My Team ────────────────────────── */}
        <Card
          className="p-5.5 flex flex-col qv-ping"
          style={{ "--ping-x": "50%", "--ping-y": "50%" }}
        >
          <div className="flex items-center gap-2.75 mb-3.5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-violet-500/12">
              <Users size={20} className="text-violet-400" />
            </div>
            <span className="text-[13px] text-(--color-text-primary)">
              My Team
            </span>
          </div>

          {data.team ? (
            <>
              <div className="qv-num text-[26px] leading-[1.1] mb-2 text-violet-400">
                {data.team.name}
              </div>
              <span className="inline-flex self-start items-center gap-1.5 px-2.5 py-0.75 rounded-full mb-3 bg-violet-500/12 border border-violet-500/25">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                <span className="qv-num text-xs font-bold text-violet-400">
                  {data.team.active}
                </span>
                <span className="text-[11px] text-violet-400">active</span>
              </span>
              <div className="flex items-center gap-2 mt-auto">
                <span className="text-[11.5px] text-(--color-text-muted)">
                  {data.team.members} members
                </span>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center flex-1 text-center py-3">
              <div className="text-[13px] text-(--color-text-muted)">
                Not assigned to a team
              </div>
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Section: Token Activity (token trend line)
   ───────────────────────────────────────────── */
function TokenActivity({ colors }) {
  const [ref, inView] = useInView(0.05);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getTokenActivity();
        if (!cancelled) setData(normalizeTokenActivity(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const tkTotal = useCountUp(data?.total ?? 0, run);
  const tkAvg = useCountUp(data?.avg ?? 0, run);
  const tkToday = useCountUp(data?.today ?? 0, run);

  /* ── Average-line plugin (drawn as dashed rule across chart) ── */
  const avgLinePlugin = useMemo(() => {
    if (!data) return null;
    return {
      id: "avgLine",
      afterDatasetsDraw(chart) {
        const { ctx, chartArea, scales } = chart;
        if (!scales.y) return;
        const y = scales.y.getPixelForValue(data.avg);
        ctx.save();
        ctx.setLineDash([4, 5]);
        ctx.strokeStyle = withAlpha(colors.blue, 0.45);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(chartArea.left, y);
        ctx.lineTo(chartArea.right, y);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = colors.blue;
        ctx.font = "600 9px monospace";
        ctx.textAlign = "right";
        ctx.fillText("AVG " + fmtK(data.avg), chartArea.right - 4, y - 5);
        ctx.restore();
      },
    };
  }, [data, colors.blue]);

  /* ── Chart.js dataset ── */
  const chartData = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.labels,
      datasets: [
        {
          data: data.series,
          fill: true,
          glowColor: colors.orange,
          borderWidth: 2.5,
          tension: 0.42,
          pointRadius: data.series.map((_, i) =>
            i === data.peakIdx || i === data.series.length - 1 ? 4 : 0,
          ),
          pointBackgroundColor: data.series.map((_, i) =>
            i === data.peakIdx
              ? colors.primary
              : i === data.series.length - 1
                ? colors.violet
                : colors.orange,
          ),
          pointBorderColor: colors.bg,
          pointBorderWidth: 2,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: "#fff",
          pointHoverBorderColor: colors.orange,
          pointHoverBorderWidth: 3,
          borderColor: (ctx) => {
            const { ctx: c, chartArea } = ctx.chart;
            if (!chartArea) return colors.orange;
            const g = c.createLinearGradient(
              chartArea.left,
              0,
              chartArea.right,
              0,
            );
            g.addColorStop(0, colors.orange);
            g.addColorStop(0.55, "#f5b40b");
            g.addColorStop(1, colors.primary);
            return g;
          },
          backgroundColor: (ctx) => {
            const { ctx: c, chartArea } = ctx.chart;
            if (!chartArea) return withAlpha(colors.orange, 0.1);
            const g = c.createLinearGradient(
              0,
              chartArea.top,
              0,
              chartArea.bottom,
            );
            g.addColorStop(0, withAlpha(colors.orange, 0.34));
            g.addColorStop(0.5, withAlpha(colors.orange, 0.1));
            g.addColorStop(1, withAlpha(colors.orange, 0));
            return g;
          },
        },
      ],
    };
  }, [data, colors]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    animation: { duration: 1400, easing: "easeOutQuart" },
    layout: { padding: { top: 8 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tipPreset(colors),
        callbacks: {
          title: (items) => data?.labels[items[0].dataIndex] ?? "",
          label: (ctx) => "  " + ctx.parsed.y.toLocaleString() + " tokens",
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxTicksLimit: 7,
          color: colors.textMuted,
          font: { size: 10 },
        },
      },
      y: {
        grid: { color: withAlpha(colors.textPrimary, 0.035) },
        border: { display: false },
        ticks: {
          color: colors.textMuted,
          padding: 8,
          callback: (v) => (v >= 1000 ? v / 1000 + "K" : v),
        },
      },
    },
  };

  /* ── Stat tile helper ── */
  const statTile = (icon, label, value, sub, accent) => (
    <div
      // NEW
      className="rounded-xl p-3 bg-(--color-bg)/60 border-l-2"
      style={{ borderColor: accent }}
    >
      <div className="flex items-center gap-1.5 text-[11px] text-(--color-text-secondary) mb-1.5">
        {icon}
        {label}
      </div>
      <div className="qv-num text-[22px]" style={{ color: accent }}>
        {value}
      </div>
      <div className="text-[10.5px] text-(--color-text-muted) mt-0.5">
        {sub}
      </div>
    </div>
  );

  const ready = !loading && !!data;

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>Token Activity</SectionLabel>
      </div>
      <Card
        className="px-6.5 py-6 qv-ping"
        style={{ "--ping-x": "100%", "--ping-y": "0%" }}
      >
        <div className="flex justify-between items-start mb-4 flex-wrap gap-3">
          <div>
            <h3 className="text-[14.5px] font-semibold flex items-center gap-2.5 text-(--color-text-primary)">
              <Activity size={19} className="text-orange-500" />
              Token Usage
            </h3>
            <p className="text-xs text-(--color-text-muted) mt-1">
              Daily consumption across the last 30 days
            </p>
          </div>
          <span className="text-[11px] text-(--color-text-secondary) font-mono">
            {ready ? data.conversations : "—"} conversations
          </span>
        </div>

        {error ? (
          <SectionError message={error} onRetry={retry} />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              {statTile(
                <Gauge size={13} className="text-orange-500" />,
                "Total used",
                ready ? tkTotal : "—",
                "across 30 days",
                colors.orange,
              )}
              {statTile(
                <Activity size={13} className="text-blue-400" />,
                "Daily avg",
                ready ? tkAvg : "—",
                "tokens / day",
                colors.blue,
              )}
              {statTile(
                <CalendarDays size={13} className="text-violet-400" />,
                "Today",
                ready ? tkToday : "—",
                ready ? `${fmtK(data.today)} used today` : "—",
                colors.violet,
              )}
            </div>
            <div className="h-50">
              {ready && chartData ? (
                <Line
                  data={chartData}
                  options={options}
                  plugins={
                    avgLinePlugin
                      ? [avgLinePlugin, lineGlowPlugin]
                      : [lineGlowPlugin]
                  }
                />
              ) : (
                <div className="h-full w-full rounded-lg bg-white/5 animate-pulse" />
              )}
            </div>
          </>
        )}
      </Card>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Section: Solo Box Progression (attempt history + completion ring)
   ───────────────────────────────────────────── */
function SoloProgression({ colors }) {
  const [ref, inView] = useInView(0.05);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getSoloProgression();
        if (!cancelled) setData(normalizeSoloProgression(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const completed = useCountUp(data?.completion.completed ?? 0, run);
  const attempted = useCountUp(data?.completion.attempted ?? 0, run);

  /* ── Chart.js dataset ── */
  const chartData = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.labels,
      datasets: [
        {
          label: "Attempted",
          data: data.attemptedCum,
          fill: true,
          borderColor: colors.primary,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.4,
          backgroundColor: (ctx) => {
            const { ctx: c, chartArea } = ctx.chart;
            if (!chartArea) return withAlpha(colors.primary, 0.1);
            const g = c.createLinearGradient(0, 0, 0, 195);
            g.addColorStop(0, withAlpha(colors.primary, 0.18));
            g.addColorStop(1, withAlpha(colors.primary, 0));
            return g;
          },
        },
        {
          label: "Completed",
          data: data.completedCum,
          fill: true,
          borderColor: colors.blue,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.4,
          backgroundColor: (ctx) => {
            const { ctx: c, chartArea } = ctx.chart;
            if (!chartArea) return withAlpha(colors.blue, 0.1);
            const g = c.createLinearGradient(0, 0, 0, 195);
            g.addColorStop(0, withAlpha(colors.blue, 0.18));
            g.addColorStop(1, withAlpha(colors.blue, 0));
            return g;
          },
        },
      ],
    };
  }, [data, colors]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { ...tipPreset(colors), mode: "index", intersect: false },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxTicksLimit: 6, color: colors.textMuted },
      },
      y: {
        grid: { color: withAlpha(colors.textPrimary, 0.04) },
        ticks: { color: colors.textMuted, stepSize: 5 },
      },
    },
  };

  /* ── Completion ring SVG values ── */
  const r = 45;
  const circ = 2 * Math.PI * r;
  const dash = ((data?.completion.pct ?? 0) / 100) * circ;

  const ready = !loading && !!data;

  /* ── Error state ── */
  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Solo Box Progression</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  /* ── Loading skeletons ── */
  if (!ready) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Solo Box Progression</SectionLabel>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Card className="p-5.5 lg:col-span-2">
            <div className="animate-pulse">
              <div className="h-4 w-48 rounded bg-white/5 mb-2" />
              <div className="h-3 w-64 rounded bg-white/5 mb-4" />
              <div className="h-48.75 rounded-lg bg-white/5" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="animate-pulse flex flex-col items-center">
              <div className="h-4 w-36 rounded bg-white/5 mb-6 self-start" />
              <div className="w-35 h-35 rounded-full bg-white/5 mb-4" />
              <div className="w-full flex flex-col gap-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-7 rounded bg-white/5" />
                ))}
              </div>
            </div>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>Solo Box Progression</SectionLabel>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* ── Attempt history chart ── */}
        <Card className="p-5.5 lg:col-span-2">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h3 className="text-[13.5px] font-semibold text-(--color-text-primary)">
                Attempt History — 7 Days
              </h3>
              <p className="text-xs text-(--color-text-muted) mt-0.5">
                Daily Box attempts and completions
              </p>
            </div>
            <div className="flex gap-3.5 text-xs">
              <span className="flex items-center gap-1.5 text-(--color-text-secondary)">
                <span className="w-2.5 h-0.75 rounded-xs bg-(--color-primary)" />
                Attempted
              </span>
              <span className="flex items-center gap-1.5 text-(--color-text-secondary)">
                <span className="w-2.5 h-0.75 rounded-xs bg-blue-500" />
                Completed
              </span>
            </div>
          </div>
          <div className="h-48.75 mt-1.5">
            <Line
              data={chartData}
              options={chartOptions}
              plugins={[lineGlowPlugin]}
            />
          </div>
        </Card>

        {/* ── Completion ring ── */}
        <Card
          className="p-6 flex flex-col items-center qv-ping"
          style={{ "--ping-x": "100%", "--ping-y": "0%" }}
        >
          <h3 className="text-[13.5px] font-semibold mb-4.5 self-start text-(--color-text-primary)">
            Completion Overview
          </h3>
          <div className="relative w-35 h-35 mb-4 transition-[filter] duration-300 hover:[filter:drop-shadow(0_0_4px_var(--color-primary))_drop-shadow(0_0_10px_var(--color-primary))]">
            <svg width="140" height="140" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r={r}
                fill="none"
                stroke={withAlpha(colors.textPrimary, 0.07)}
                strokeWidth="8"
              />
              <circle
                cx="50"
                cy="50"
                r={r}
                fill="none"
                stroke={colors.primary}
                strokeWidth="8"
                strokeLinecap="round"
                transform="rotate(-90 50 50)"
                strokeDasharray={`${run ? dash : 0} ${circ}`}
                style={{
                  transition: "stroke-dasharray 1.2s cubic-bezier(.22,1,.36,1)",
                }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="qv-num text-[26px] text-(--color-primary)">
                {data.completion.pct}%
              </span>
              <span className="text-[10px] text-(--color-text-muted) font-mono uppercase tracking-[0.08em] mt-0.5">
                Complete
              </span>
            </div>
          </div>
          <div className="w-full flex flex-col gap-2">
            <div className="flex justify-between text-[12.5px] py-1.5 border-b border-(--color-border)">
              <span className="flex items-center gap-1.5 text-(--color-text-secondary)">
                <span className="w-2 h-2 rounded-full bg-(--color-primary)" />
                Completed
              </span>
              <span className="font-semibold text-(--color-primary)">
                {completed}
              </span>
            </div>
            <div className="flex justify-between text-[12.5px] py-1.5 border-b border-(--color-border)">
              <span className="flex items-center gap-1.5 text-(--color-text-secondary)">
                <span className="w-2 h-2 rounded-full bg-blue-500" />
                Untouched
              </span>
              <span className="font-semibold text-blue-400">
                {data.completion.untouched}
              </span>
            </div>
            <div className="flex justify-between text-[12.5px] py-1.5 border-b border-(--color-border)">
              <span className="flex items-center gap-1.5 text-(--color-text-secondary)">
                <span className="w-2 h-2 rounded-full bg-orange-500" />
                Attempted
              </span>
              <span className="font-semibold text-(--color-text-primary)">
                {attempted}
              </span>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Section: Category Coverage & Recommendations
   ───────────────────────────────────────────── */
function CategoryCoverage({ colors }) {
  const [ref] = useInView(0.05);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getCategoryCoverage();
        if (!cancelled) setData(normalizeCategoryCoverage(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  /* ── Color helper: green ≥75, blue ≥40, orange >0, red =0 ── */
  const colorFor = (v, hover) =>
    v >= 75
      ? withAlpha(colors.primary, hover ? 0.85 : 0.65)
      : v >= 40
        ? withAlpha(colors.blue, hover ? 0.8 : 0.6)
        : v > 0
          ? withAlpha(colors.orange, hover ? 0.8 : 0.6)
          : withAlpha(colors.red, hover ? 0.6 : 0.4);

  /* ── Chart.js dataset ── */
  const chartData = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.chart.labels,
      datasets: [
        {
          data: data.chart.values,
          backgroundColor: data.chart.values.map((v) => colorFor(v, false)),
          hoverBackgroundColor: data.chart.values.map((v) => colorFor(v, true)),
          borderRadius: 5,
          borderSkipped: false,
        },
      ],
    };
  }, [data, colors]);

  const chartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tipPreset(colors),
        callbacks: {
          label: (ctx) => ` ${ctx.parsed.x}% complete`,
        },
      },
    },
    scales: {
      x: {
        max: 100,
        grid: { color: withAlpha(colors.textPrimary, 0.04) },
        ticks: {
          color: colors.textMuted,
          callback: (v) => v + "%",
        },
      },
      y: {
        grid: { display: false },
        ticks: { color: colors.textSecondary, font: { size: 11 } },
      },
    },
  };

  const ready = !loading && !!data;

  /* ── Error state ── */
  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Category Coverage &amp; Recommendations</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  /* ── Loading skeletons ── */
  if (!ready) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Category Coverage &amp; Recommendations</SectionLabel>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Card className="p-5.5 lg:col-span-2">
            <div className="animate-pulse">
              <div className="h-4 w-52 rounded bg-white/5 mb-4" />
              <div className="h-65 rounded-lg bg-white/5" />
            </div>
          </Card>
          <div className="flex flex-col gap-4.5">
            {[0, 1].map((i) => (
              <Card key={i} className="p-5">
                <div className="animate-pulse flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/5" />
                    <div className="h-8 w-16 rounded bg-white/5" />
                  </div>
                  <div className="h-5 w-40 rounded bg-white/5" />
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>Category Coverage &amp; Recommendations</SectionLabel>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* ── Bar chart ── */}
        <Card className="p-5.5 lg:col-span-2">
          <h3 className="text-[13.5px] font-semibold mb-3.5 text-(--color-text-primary)">
            Completion % by Category
          </h3>
          <div className="h-65">
            <Bar
              data={chartData}
              options={chartOptions}
              plugins={[barGlowPlugin]}
            />
          </div>
        </Card>

        {/* ── Recommendation cards ── */}
        <div className="flex flex-col gap-4.5">
          {/* Fully Mastered */}
          <Card className="p-5">
            <div className="flex items-center gap-3 mb-3.5">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-(--color-primary)/15">
                <Trophy size={22} className="text-(--color-primary)" />
              </div>
              <div>
                <div className="text-[10.5px] font-mono uppercase tracking-widest text-(--color-text-muted) font-semibold">
                  Fully Mastered
                </div>
                <div className="qv-num text-[28px] text-(--color-primary)">
                  {data.mastered.length}
                </div>
              </div>
            </div>
            {data.mastered.length > 0 ? (
              <div className="flex flex-col gap-1.5 text-[12.5px] text-(--color-text-secondary)">
                {data.mastered.map((m) => (
                  <div key={m} className="flex items-center gap-1.5">
                    <Check size={15} className="text-(--color-primary)" />
                    {m}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[12.5px] text-(--color-text-muted)">
                No categories fully mastered yet.
              </div>
            )}
          </Card>

          {/* Untouched */}
          <Card className="p-5">
            <div className="flex items-center gap-3 mb-3.5">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-500/12">
                <Compass size={22} className="text-red-500" />
              </div>
              <div>
                <div className="text-[10.5px] font-mono uppercase tracking-widest text-(--color-text-muted) font-semibold">
                  Untouched
                </div>
                <div className="qv-num text-[28px] text-red-500">
                  {data.untouched.length}
                </div>
              </div>
            </div>
            {data.untouched.length > 0 ? (
              <div className="flex flex-col gap-2">
                {data.untouched.map((u) => (
                  <div
                    key={u}
                    className="rounded-lg p-2.5 text-[12.5px] bg-red-500/12 border border-red-500/20"
                  >
                    <div className="text-red-500 font-semibold">{u}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[12.5px] text-(--color-text-muted)">
                All categories have been started!
              </div>
            )}
          </Card>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Section: My Boxes (flow-state donut + run history)
   ───────────────────────────────────────────── */
function MyBoxes({ colors }) {
  const [ref, inView] = useInView(0.05);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getMyBoxes(20, 0);
        if (!cancelled) setData(normalizeMyBoxes(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const total = useCountUp(data?.counts.total ?? 0, run);
  const [donutHover, setDonutHover] = useState(false);

  /* ── Donut chart ── */
  const donutData = useMemo(() => {
    if (!data) return null;
    const { approved, pending, rejected } = data.counts;
    const allZero = !approved && !pending && !rejected;
    return {
      labels: ["Approved", "Pending", "Rejected"],
      datasets: [
        {
          data: allZero ? [1] : [approved, pending, rejected],
          backgroundColor: allZero
            ? [withAlpha(colors.primary, 0.12)]
            : [colors.primary, colors.orange, colors.red],
          borderWidth: 0,
          hoverBorderWidth: 0,
          borderRadius: 4,
          hoverOffset: allZero ? 0 : 6,
        },
      ],
    };
  }, [data, colors]);

  const donutOptions = {
    cutout: "70%",
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: tipPreset(colors) },
  };

  /* ── Status pill config ── */
  const STATUS_CFG = {
    approved: {
      color: "var(--color-primary)",
      bg: "color-mix(in srgb, var(--color-primary) 10%, transparent)",
      border: "color-mix(in srgb, var(--color-primary) 20%, transparent)",
      label: "APPROVED",
    },
    pending: {
      color: "#f59e0b",
      bg: "rgb(245 158 11 / 0.12)",
      border: "rgb(245 158 11 / 0.2)",
      label: "PENDING",
    },
    rejected: {
      color: "#ef4444",
      bg: "rgb(239 68 68 / 0.12)",
      border: "rgb(239 68 68 / 0.2)",
      label: "REJECTED",
    },
  };

  /* ── Legend row helper ── */
  const legendRow = (label, value, color, bgClass, borderClass) => (
    <div
      className={`flex justify-between items-center text-[13px] px-2.5 py-2 rounded-lg ${bgClass} border ${borderClass}`}
    >
      <span className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />
        {label}
      </span>
      <span className="qv-num font-bold text-[16px]" style={{ color }}>
        {value}
      </span>
    </div>
  );

  const ready = !loading && !!data;

  /* ── Error state ── */
  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>My Boxes</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  /* ── Loading skeleton ── */
  if (!ready) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>My Boxes</SectionLabel>
        </div>
        <Card className="px-8 py-7">
          <div className="animate-pulse">
            <div className="flex justify-between mb-6">
              <div className="h-5 w-28 rounded bg-white/5" />
              <div className="h-4 w-24 rounded bg-white/5" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[220px_1px_1fr] gap-6">
              <div className="flex flex-col items-center gap-4">
                <div className="w-27.5 h-27.5 rounded-full bg-white/5" />
                <div className="w-full flex flex-col gap-2">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="h-9 rounded-lg bg-white/5" />
                  ))}
                </div>
              </div>
              <div />
              <div className="flex flex-col gap-3">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-9 rounded bg-white/5" />
                ))}
              </div>
            </div>
          </div>
        </Card>
      </section>
    );
  }

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>My Boxes</SectionLabel>
      </div>
      <Card className="px-8 py-7">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-[15px] font-semibold text-(--color-text-primary)">
            My Boxes
          </h3>
          <span className="text-[11.5px] text-(--color-text-muted) font-mono">
            {data.counts.total} TOTAL RUNS
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1px_1fr] gap-0 items-start">
          {/* ── Left: donut + legend ── */}
          <div className="lg:pr-7">
            <div
              className="relative w-27.5 h-27.5 mx-auto mb-5 transition-[filter] duration-300"
              onMouseEnter={() => setDonutHover(true)}
              onMouseLeave={() => setDonutHover(false)}
              style={{
                filter: donutHover
                  ? `drop-shadow(0 0 8px ${colors.primary}) drop-shadow(0 0 18px ${withAlpha(colors.primary, 0.35)})`
                  : "none",
              }}
            >
              <Doughnut data={donutData} options={donutOptions} />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="qv-num text-[22px] text-(--color-text-primary)">
                  {total}
                </span>
                <span className="text-[9px] text-(--color-text-muted) font-mono uppercase">
                  Total
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {legendRow(
                "Approved",
                data.counts.approved,
                colors.primary,
                "bg-(--color-primary)/[0.06]",
                "border-(--color-primary)/15",
              )}
              {legendRow(
                "Pending",
                data.counts.pending,
                colors.orange,
                "bg-orange-500/12",
                "border-orange-500/15",
              )}
              {legendRow(
                "Rejected",
                data.counts.rejected,
                colors.red,
                "bg-red-500/12",
                "border-red-500/15",
              )}
            </div>
          </div>

          {/* ── Divider ── */}
          <div className="hidden lg:block bg-(--color-border) w-px h-full min-h-65" />

          {/* ── Right: run history ── */}
          <div className="lg:pl-7 mt-6 lg:mt-0">
            <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-(--color-text-muted) font-semibold mb-3">
              Box Run History
            </div>
            <div className="grid grid-cols-[2fr_1fr_100px] gap-2 px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest text-(--color-text-muted) font-semibold border-b border-(--color-border) mb-1">
              <span>Box</span>
              <span>Time</span>
              <span className="text-center">Status</span>
            </div>

            {data.boxes.length === 0 ? (
              <div className="px-3 py-8 text-[13px] text-(--color-text-muted) text-center">
                No boxes created yet.
              </div>
            ) : (
              data.boxes.map((r) => {
                const s = STATUS_CFG[r.status] ?? STATUS_CFG.pending;
                return (
                  <div
                    key={r.id}
                    className="grid grid-cols-[2fr_1fr_100px] gap-2 px-3 py-2.5 text-[12.5px] border-b border-(--color-border) rounded-md hover:bg-white/2.5 transition-colors"
                  >
                    <span
                      className="text-(--color-text-primary) font-medium font-mono text-[11.5px] truncate"
                      title={r.name}
                    >
                      {r.name}
                    </span>
                    <span className="text-(--color-text-muted)">{r.time}</span>
                    <span className="text-center">
                      <span
                        className="px-2 py-0.5 rounded-full text-[10px] font-bold font-mono tracking-[0.04em] border"
                        style={{
                          color: s.color,
                          background: s.bg,
                          borderColor: s.border,
                        }}
                      >
                        {s.label}
                      </span>
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </Card>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Section: TKSA Progress
   ───────────────────────────────────────────── */

function TksaProgress({ colors }) {
  const [ref, inView] = useInView(0.1);
  const [activeFramework, setActiveFramework] = useState(FRAMEWORKS[0].code);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getTksaProgress(activeFramework);
        if (!cancelled) setData(normalizeTksaProgress(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeFramework, reload]);

  const run = inView && !!data;
  const covered = useCountUp(data?.itemsCovered ?? 0, run);
  const total = useCountUp(data?.itemsTotal ?? 0, run);

  /* ── Coverage ring SVG values ── */
  const r = 45;
  const circ = 2 * Math.PI * r;
  const dash = ((data?.coveragePct ?? 0) / 100) * circ;

  /* ── TKSA bucket config ── */
  const buckets = data
    ? [
        {
          key: "task",
          label: "Tasks",
          icon: <Wrench size={18} />,
          color: colors.primary,
          ...data.task,
        },
        {
          key: "knowledge",
          label: "Knowledge",
          icon: <BookOpen size={18} />,
          color: colors.blue,
          ...data.knowledge,
        },
        {
          key: "skill",
          label: "Skills",
          icon: <Zap size={18} />,
          color: colors.orange,
          ...data.skill,
        },
        {
          key: "ability",
          label: "Abilities",
          icon: <Brain size={18} />,
          color: colors.violet,
          ...data.ability,
        },
      ]
    : [];

  const ready = !loading && !!data;

  /* ── Error state ── */
  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Framework Alignment</SectionLabel>
        </div>
        <Card>
          {/* Framework tabs even in error so user can switch */}
          <div className="flex items-center gap-2 px-5.5 pt-5 pb-3 border-b border-(--color-border)">
            {FRAMEWORKS.map((fw) => (
              <button
                key={fw.code}
                onClick={() => setActiveFramework(fw.code)}
                className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold transition-all ${
                  activeFramework === fw.code
                    ? "bg-(--color-primary)/15 text-(--color-primary) border border-(--color-primary)/30"
                    : "bg-white/4 text-(--color-text-secondary) border border-transparent hover:bg-white/6"
                }`}
              >
                {fw.label}
              </button>
            ))}
          </div>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  /* ── Loading skeleton ── */
  if (!ready) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Framework Alignment</SectionLabel>
        </div>
        <Card>
          <div className="flex items-center gap-2 px-5.5 pt-5 pb-3 border-b border-(--color-border)">
            {FRAMEWORKS.map((fw) => (
              <button
                key={fw.code}
                onClick={() => setActiveFramework(fw.code)}
                className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold transition-all ${
                  activeFramework === fw.code
                    ? "bg-(--color-primary)/15 text-(--color-primary) border border-(--color-primary)/30"
                    : "bg-white/4 text-(--color-text-secondary) border border-transparent hover:bg-white/6"
                }`}
              >
                {fw.label}
              </button>
            ))}
          </div>
          <div className="px-7.5 py-6.5 animate-pulse flex items-center gap-8.5 flex-wrap">
            <div className="w-37.5 h-37.5 rounded-full bg-white/5 shrink-0" />
            <div className="flex-1 min-w-65">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="h-24 rounded-xl bg-white/5" />
                ))}
              </div>
            </div>
          </div>
        </Card>
      </section>
    );
  }

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>Framework Alignment</SectionLabel>
      </div>
      <Card>
        {/* ── Framework tabs ── */}
        <div className="flex items-center gap-2 px-5.5 pt-5 pb-3 border-b border-(--color-border)">
          {FRAMEWORKS.map((fw) => (
            <button
              key={fw.code}
              onClick={() => setActiveFramework(fw.code)}
              className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold transition-all ${
                activeFramework === fw.code
                  ? "bg-(--color-primary)/15 text-(--color-primary) border border-(--color-primary)/30"
                  : "bg-white/4 text-(--color-text-secondary) border border-transparent hover:bg-white/6"
              }`}
            >
              {fw.label}
            </button>
          ))}
        </div>

        <div className="px-7.5 py-6.5">
          {/* Header */}
          <div className="flex items-center gap-2.5 mb-5">
            <Shield size={18} className="text-(--color-primary)" />
            <div>
              <h3 className="text-[13.5px] font-semibold text-(--color-text-primary)">
                {data.frameworkName}
              </h3>
              <p className="text-xs text-(--color-text-muted) mt-0.5">
                TKSA coverage based on your completed scenarios
              </p>
            </div>
          </div>

          <div className="flex items-center gap-8.5 flex-wrap">
            {/* ── Coverage ring ── */}
            <div className="relative w-37.5 h-37.5 shrink-0">
              <svg
                width="150"
                height="150"
                viewBox="0 0 100 100"
                className="w-full h-full transition-[filter] duration-300 hover:[filter:drop-shadow(0_0_4px_var(--color-primary))_drop-shadow(0_0_10px_var(--color-primary))]"
              >
                <circle
                  cx="50"
                  cy="50"
                  r={r}
                  fill="none"
                  stroke={withAlpha(colors.textPrimary, 0.07)}
                  strokeWidth="8"
                />
                <circle
                  cx="50"
                  cy="50"
                  r={r}
                  fill="none"
                  stroke={colors.primary}
                  strokeWidth="8"
                  strokeLinecap="round"
                  transform="rotate(-90 50 50)"
                  strokeDasharray={`${run ? dash : 0} ${circ}`}
                  style={{
                    transition:
                      "stroke-dasharray 1.2s cubic-bezier(.22,1,.36,1)",
                  }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="qv-num text-[30px] text-(--color-primary)">
                  {data.coveragePct}%
                </span>
                <span className="text-[9px] tracking-[0.12em] uppercase text-(--color-text-secondary) font-mono mt-0.5">
                  coverage
                </span>
              </div>
            </div>

            {/* ── TKSA breakdown ── */}
            <div className="flex-1 min-w-65">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {buckets.map((b) => (
                  <div
                    key={b.key}
                    className="px-4 py-3.5 rounded-xl border border-(--color-border) bg-(--color-bg)/60"
                  >
                    <div className="flex items-center gap-2 mb-2.5">
                      <span style={{ color: b.color }}>{b.icon}</span>
                      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-(--color-text-muted) font-mono">
                        {b.label}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-1.5 mb-2">
                      <span
                        className="qv-num text-[22px] font-semibold"
                        style={{ color: b.color }}
                      >
                        {b.covered}
                      </span>
                      <span className="text-[12px] text-(--color-text-muted)">
                        / {b.total}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden bg-(--color-border)/50">
                      <div
                        className="h-full rounded-full transition-all duration-1000 ease-out"
                        style={{
                          width: run ? `${b.pct}%` : "0%",
                          background: b.color,
                          minWidth: b.pct > 0 ? "4px" : 0,
                        }}
                      />
                    </div>
                    <div className="text-[10px] text-(--color-text-muted) mt-1.5 text-right font-mono">
                      {b.pct}%
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary line */}
              <div className="mt-3.5 text-xs text-(--color-text-secondary) flex items-center gap-2 flex-wrap">
                <span className="w-2.25 h-2.25 rounded-xs bg-(--color-primary)" />
                {covered} of {total} framework items covered
                <span className="text-(--color-text-muted)">·</span>
                <span className="text-(--color-text-muted) font-mono text-[11px]">
                  {data.frameworkCode.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </section>
  );
}
/* ─────────────────────────────────────────────
   Section: Difficulty stats
   ───────────────────────────────────────────── */

function DifficultyStats({ colors }) {
  const [ref, inView] = useInView(0.1);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getDifficultyStats();
        if (!cancelled) setData(normalizeDifficultyStats(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const totalCompleted = useCountUp(data?.totalCompleted ?? 0, run);

  const DIFF_CFG = {
    easy: {
      label: "Easy",
      color: colors.primary,
      bg: "bg-(--color-primary)/15",
      iconBg: "bg-(--color-primary)/15",
    },
    medium: {
      label: "Medium",
      color: colors.orange,
      bg: "bg-orange-500/15",
      iconBg: "bg-orange-500/15",
    },
    hard: {
      label: "Hard",
      color: colors.red,
      bg: "bg-red-500/15",
      iconBg: "bg-red-500/15",
    },
  };

  const ready = !loading && !!data;

  /* ── Error state ── */
  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Difficulty Breakdown</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  /* ── Loading skeleton ── */
  if (!ready) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Difficulty Breakdown</SectionLabel>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4.5">
          {[0, 1, 2].map((i) => (
            <Card key={i} className="p-5.5">
              <div className="animate-pulse flex flex-col gap-3">
                <div className="flex items-center gap-2.75">
                  <div className="w-10 h-10 rounded-full bg-white/5" />
                  <div className="h-3 w-20 rounded bg-white/5" />
                </div>
                <div className="h-10 w-16 rounded bg-white/5" />
                <div className="h-1.5 rounded-full bg-white/5" />
                <div className="h-14 rounded-xl bg-white/5" />
              </div>
            </Card>
          ))}
        </div>
      </section>
    );
  }

  /* ── Max completed for relative bar sizing ── */
  const maxCompleted = Math.max(...data.buckets.map((b) => b.completed), 1);

  return (
    <section ref={ref}>
      <div className="flex items-center gap-2.5 mb-4">
        <SectionLabel>Difficulty Breakdown</SectionLabel>
        <span className="inline-flex items-center px-2.25 py-0.75 rounded-md text-[11px] font-semibold bg-(--color-primary)/10 text-(--color-primary) border border-(--color-primary)/30">
          {totalCompleted} solved
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4.5">
        {data.buckets.map((b) => {
          const cfg = DIFF_CFG[b.difficulty];
          const barPct =
            maxCompleted > 0
              ? Math.max(
                  (b.completed / maxCompleted) * 100,
                  b.completed > 0 ? 4 : 0,
                )
              : 0;

          return (
            <Card
              key={b.difficulty}
              className="p-5.5 qv-ping"
              style={{ "--ping-x": "85%", "--ping-y": "20%" }}
            >
              {/* Header */}
              <div className="flex items-center gap-2.75 mb-3.5">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${cfg.iconBg}`}
                >
                  <BarChart3 size={18} style={{ color: cfg.color }} />
                </div>
                <span className="text-[13px] font-semibold text-(--color-text-primary)">
                  {cfg.label}
                </span>
              </div>

              {/* Completed count */}
              <div
                className="qv-num text-[36px] mb-1"
                style={{ color: cfg.color }}
              >
                {b.completed}
              </div>
              <div className="text-[12px] text-(--color-text-muted) mb-3">
                boxes completed
              </div>

              {/* Relative bar */}
              <div className="h-1.5 rounded-full overflow-hidden bg-white/5 mb-4">
                <div
                  className="h-full rounded-full transition-all duration-1000 ease-out"
                  style={{
                    width: run ? `${barPct}%` : "0%",
                    background: cfg.color,
                  }}
                />
              </div>

              {/* Avg completion time tile */}
              <div // NEW
                className="rounded-xl px-3.5 py-3 border border-(--color-border) bg-(--color-bg)/60"
              >
                <div className="flex items-center gap-1.5 text-[10.5px] text-(--color-text-muted) mb-1">
                  <Timer size={13} style={{ color: cfg.color }} />
                  <span className="font-mono uppercase tracking-[0.06em] font-semibold">
                    Avg Time
                  </span>
                </div>
                <div
                  className="qv-num text-[20px] font-semibold"
                  style={{
                    color: b.avgMinutes > 0 ? cfg.color : colors.textMuted,
                  }}
                >
                  {b.avgLabel}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </section>
  );
}

const FRAMEWORKS = [
  { code: "scywf_v1", label: "SCyWF" },
  { code: "nist_sp_800_181", label: "NIST 800-181" },
];

function TksaBreakdown({ colors }) {
  const [ref] = useInView(0.05);
  const [activeFramework, setActiveFramework] = useState(FRAMEWORKS[0].code);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  const [openGroups, setOpenGroups] = useState({});
  const [openRoles, setOpenRoles] = useState({});
  const [showAll, setShowAll] = useState(false);
  const VISIBLE = 5;

  const toggleGroup = (code) =>
    setOpenGroups((prev) => ({ ...prev, [code]: !prev[code] }));
  const toggleRole = (code) =>
    setOpenRoles((prev) => ({ ...prev, [code]: !prev[code] }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getTksaBreakdown(activeFramework);
        if (!cancelled) {
          setData(normalizeTksaBreakdown(raw));
          setOpenGroups({});
          setOpenRoles({});
          setShowAll(false);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeFramework, reload]);

  const ready = !loading && !!data;

  /* ── TKSA mini-bar row (reused at every level) ── */
  const TksaBars = ({ byType }) => {
    const items = [
      { key: "T", ...byType.task, color: colors.primary },
      { key: "K", ...byType.knowledge, color: colors.blue },
      { key: "S", ...byType.skill, color: colors.orange },
      { key: "A", ...byType.ability, color: colors.violet },
    ];
    return (
      <div className="flex items-center gap-3 flex-wrap">
        {items.map((b) => (
          <div key={b.key} className="flex items-center gap-1.5">
            <span
              className="text-[10px] font-bold font-mono w-3"
              style={{ color: b.color }}
            >
              {b.key}
            </span>
            <div className="w-16 h-1.5 rounded-full overflow-hidden bg-(--color-border)/50">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${b.pct}%`,
                  background: b.color,
                  minWidth: b.pct > 0 ? "3px" : 0,
                }}
              />
            </div>
            <span className="text-[10px] text-(--color-text-muted) font-mono w-12">
              {b.covered}/{b.total}
            </span>
          </div>
        ))}
      </div>
    );
  };

  /* ── Framework tabs (shared across all states) ── */
  const FrameworkTabs = () => (
    <div className="flex items-center gap-2 px-5.5 pt-5 pb-3 border-b border-(--color-border)">
      {FRAMEWORKS.map((fw) => (
        <button
          key={fw.code}
          onClick={() => setActiveFramework(fw.code)}
          className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold transition-all ${
            activeFramework === fw.code
              ? "bg-(--color-primary)/15 text-(--color-primary) border border-(--color-primary)/30"
              : "bg-white/4 text-(--color-text-secondary) border border-transparent hover:bg-white/6"
          }`}
        >
          {fw.label}
        </button>
      ))}

      {ready && (
        <div className="ml-auto flex items-center gap-2">
          <span className="qv-num text-[18px] text-(--color-primary) font-semibold">
            {data.frameworkCoverage.coveragePct}%
          </span>
          <span className="text-[11px] text-(--color-text-muted)">
            overall ({data.frameworkCoverage.itemsCovered}/
            {data.frameworkCoverage.itemsTotal})
          </span>
        </div>
      )}
    </div>
  );

  /* ── Error state ── */
  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>TKSA Breakdown by Framework, Specialty and Job Role</SectionLabel>
        </div>
        <Card className="p-0 overflow-hidden">
          <FrameworkTabs />
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  /* ── Loading skeleton ── */
  if (!ready) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>TKSA Breakdown by Framework, Specialty and Job Role</SectionLabel>
        </div>
        <Card className="p-0 overflow-hidden">
          <FrameworkTabs />
          <div className="p-5.5 flex flex-col gap-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-14 rounded-xl bg-white/5 animate-pulse"
              />
            ))}
          </div>
        </Card>
      </section>
    );
  }

  /* ── Visible groups ── */
  const visibleGroups = showAll ? data.groups : data.groups.slice(0, VISIBLE);
  const extra = data.groups.length - VISIBLE;

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>TKSA Breakdown by Framework, Specialty and Job Role</SectionLabel>
      </div>
      <Card className="p-0 overflow-hidden">
        <FrameworkTabs />

        {data.groups.length === 0 ? (
          <div className="px-5.5 py-10 text-[13px] text-(--color-text-muted) text-center">
            No specialty data available for this framework.
          </div>
        ) : (
          <>
            <div className="divide-y divide-(--color-border)">
              {visibleGroups.map((group) => {
                const isOpen = !!openGroups[group.code];
                return (
                  <div key={group.code}>
                    {/* ── Specialty row ── */}
                    <button
                      onClick={() => toggleGroup(group.code)}
                      className="w-full flex items-center gap-3 px-5.5 py-4 text-left hover:bg-white/2 transition-colors"
                    >
                      <ChevronRight
                        size={16}
                        className={`text-(--color-text-muted) shrink-0 transition-transform duration-200 ${
                          isOpen ? "rotate-90" : ""
                        }`}
                      />
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-(--color-primary)/10 shrink-0">
                        <Layers size={15} className="text-(--color-primary)" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[13px] font-semibold text-(--color-text-primary) truncate">
                            {group.name}
                          </span>
                          <span className="text-[10px] text-(--color-text-muted) font-mono">
                            {group.code}
                          </span>
                        </div>
                        <span className="text-[11px] text-(--color-text-muted)">
                          {group.parentName}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <div className="hidden sm:block">
                          <TksaBars byType={group.coverage.byType} />
                        </div>
                        <div className="text-right min-w-14">
                          <span
                            className="qv-num text-[15px] font-semibold"
                            style={{
                              color:
                                group.coverage.coveragePct >= 50
                                  ? colors.primary
                                  : group.coverage.coveragePct >= 20
                                    ? colors.orange
                                    : colors.textSecondary,
                            }}
                          >
                            {group.coverage.coveragePct}%
                          </span>
                          <div className="text-[9.5px] text-(--color-text-muted) font-mono">
                            {group.coverage.itemsCovered}/
                            {group.coverage.itemsTotal}
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* ── Expanded: work roles ── */}
                    {isOpen && (
                      <div className="bg-(--color-surface)/50">
                        {group.workRoles.map((role) => {
                          const roleOpen = !!openRoles[role.code];
                          return (
                            <div key={role.code}>
                              {/* Work role row */}
                              <button
                                onClick={() => toggleRole(role.code)}
                                className="w-full flex items-center gap-3 pl-14 pr-5.5 py-3.5 text-left hover:bg-white/2 transition-colors border-t border-(--color-border)/50"
                              >
                                <ChevronRight
                                  size={14}
                                  className={`text-(--color-text-muted) shrink-0 transition-transform duration-200 ${
                                    roleOpen ? "rotate-90" : ""
                                  }`}
                                />
                                <div className="w-6.5 h-6.5 rounded-md flex items-center justify-center bg-blue-500/10 shrink-0">
                                  <Target size={13} className="text-blue-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <span className="text-[12.5px] font-medium text-(--color-text-primary) truncate block">
                                    {role.name}
                                  </span>
                                  <span className="text-[10px] text-(--color-text-muted) font-mono">
                                    {role.code}
                                  </span>
                                </div>
                                <div className="text-right min-w-14 shrink-0">
                                  <span
                                    className="qv-num text-[14px] font-semibold"
                                    style={{
                                      color:
                                        role.coverage.coveragePct >= 50
                                          ? colors.primary
                                          : role.coverage.coveragePct >= 20
                                            ? colors.orange
                                            : colors.textSecondary,
                                    }}
                                  >
                                    {role.coverage.coveragePct}%
                                  </span>
                                  <div className="text-[9.5px] text-(--color-text-muted) font-mono">
                                    {role.coverage.itemsCovered}/
                                    {role.coverage.itemsTotal}
                                  </div>
                                </div>
                              </button>

                              {/* Expanded: TKSA detail cards */}
                              {roleOpen && (
                                <div className="pl-24 pr-5.5 pb-4 pt-1">
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                                    {[
                                      {
                                        label: "Tasks",
                                        icon: <Wrench size={15} />,
                                        color: colors.primary,
                                        ...role.coverage.byType.task,
                                      },
                                      {
                                        label: "Knowledge",
                                        icon: <BookOpen size={15} />,
                                        color: colors.blue,
                                        ...role.coverage.byType.knowledge,
                                      },
                                      {
                                        label: "Skills",
                                        icon: <Zap size={15} />,
                                        color: colors.orange,
                                        ...role.coverage.byType.skill,
                                      },
                                      {
                                        label: "Abilities",
                                        icon: <Brain size={15} />,
                                        color: colors.violet,
                                        ...role.coverage.byType.ability,
                                      },
                                    ].map((b) => (
                                      <div
                                        key={b.label}
                                        className="rounded-lg px-3 py-2.5 border border-(--color-border) bg-(--color-bg)/60 transition-[filter,transform,border-color] duration-75 hover:-translate-y-0.5 hover:border-[var(--tksa-glow)]/40 hover:[filter:drop-shadow(0_0_2px_color-mix(in_srgb,var(--tksa-glow)_55%,transparent))]"
                                        style={{ "--tksa-glow": b.color }}
                                      >
                                        <div className="flex items-center gap-1.5 mb-1.5">
                                          <span style={{ color: b.color }}>
                                            {b.icon}
                                          </span>
                                          <span className="text-[9.5px] font-mono uppercase tracking-[0.06em] font-semibold text-(--color-text-muted)">
                                            {b.label}
                                          </span>
                                        </div>
                                        <div className="flex items-baseline gap-1 mb-1.5">
                                          <span
                                            className="qv-num text-[18px] font-semibold"
                                            style={{ color: b.color }}
                                          >
                                            {b.covered}
                                          </span>
                                          <span className="text-[11px] text-(--color-text-muted)">
                                            / {b.total}
                                          </span>
                                        </div>
                                        <div className="h-1.25 rounded-full overflow-hidden bg-(--color-border)/50">
                                          <div
                                            className="h-full rounded-full"
                                            style={{
                                              width: `${b.pct}%`,
                                              background: b.color,
                                              minWidth: b.pct > 0 ? "3px" : 0,
                                            }}
                                          />
                                        </div>
                                        <div className="text-[9px] font-mono text-(--color-text-muted) text-right mt-1">
                                          {b.pct}%
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── Show more / less button ── */}
            {extra > 0 && (
              <div className="flex justify-center p-3.5 border-t border-(--color-border)">
                <button
                  onClick={() => setShowAll((o) => !o)}
                  className="inline-flex items-center gap-2 pl-4 pr-2 py-1.75 rounded-full text-xs font-semibold text-(--color-primary) bg-(--color-primary)/6 border border-(--color-primary)/15 hover:bg-(--color-primary)/10 hover:-translate-y-px transition-all"
                >
                  <span>{showAll ? "Show less" : "Show more"}</span>
                  {!showAll && (
                    <span className="inline-flex items-center justify-center min-w-5.5 h-5.5 px-1.5 rounded-full bg-(--color-primary)/15 text-(--color-primary) qv-num text-[11px]">
                      {extra}
                    </span>
                  )}
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-300 ${
                      showAll ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>
            )}
          </>
        )}
      </Card>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Page
   ───────────────────────────────────────────── */
export default function CompanyUserDashboard() {
  const colors = useThemeColors();
  const { user } = useAuth();
  const displayName = user?.name || user?.username || "User";

  return (
    <CompanyUserLayout>
      <div className="px-8 py-8 space-y-7">
        {/* Greeting */}
        <div>
          <div className="text-[10.5px] tracking-[0.22em] uppercase text-(--color-text-muted) flex items-center gap-2 font-mono">
            <StatusDot variant="live" size={7} /> My Dashboard · Alpha Squad
          </div>
          <h1 className="qv-serif mt-2 text-[40px] leading-[1.05] font-medium tracking-tight text-(--color-text-primary)">
            Welcome back,{" "}
            <span className="text-(--color-primary)">{displayName}</span>.
          </h1>
          <p className="mt-2 text-sm text-(--color-text-secondary)">
            Your personal progress, activity, and team stats at a glance.
          </p>
        </div>

        {colors && (
          <>
            <MyOverview colors={colors} />
            <TokenActivity colors={colors} />
            <SoloProgression colors={colors} />
            <DifficultyStats colors={colors} />
            <CategoryCoverage colors={colors} />
            <TksaProgress colors={colors} />
            <TksaBreakdown colors={colors} />
            <MyBoxes colors={colors} />
          </>
        )}
      </div>
    </CompanyUserLayout>
  );
}
